import { CheckCircle2, Circle, LoaderCircle, MonitorSmartphone } from 'lucide-react'
import type { ReactNode } from 'react'

import '../../../../shared/ui/aqsi.css'
import type { OpeningStepState, RmkActions, RmkVm } from '../../model/types'
import { FeedbackHelp } from '../FeedbackHelp'
import { OperationPanel } from '../OperationPanel'
import { elapsedText, useNow } from '../useNow'
import '../rmk.css'

const STEP_ICONS: Readonly<Record<OpeningStepState, ReactNode>> = {
  done: <CheckCircle2 size={18} aria-hidden="true" />,
  active: <LoaderCircle className="aqsi-spinner" size={18} aria-hidden="true" />,
  pending: <Circle size={18} aria-hidden="true" />,
}

/** Слово состояния рядом со значком: экран в `grayscale(1)` читается и без цвета и без формы значка. */
const STEP_WORDS: Readonly<Record<OpeningStepState, string>> = { done: 'готово', active: 'идёт', pending: 'дальше' }

/**
 * Ожидание открытия смены (RP-FB22 3): строки хода «Проверяем связь → Отправили команду кассе → Ждём ответ кассы» с
 * отсчётом времени. Касса может спросить подтверждение на своём экране — об этом сказано словами. Никаких «Выполняется…»
 * без конца: после срока команды появляется «Исход неизвестен» с «Проверить результат» и ручной сверкой.
 */
export function OpeningScreen({ vm, actions }: { vm: RmkVm; actions: RmkActions }) {
  const opening = vm.opening
  const now = useNow(1000, opening !== null)
  if (opening === null) return null
  const operation = vm.operation
  const attention =
    operation !== null && (operation.manualRequired || operation.tone === 'warning' || operation.tone === 'danger')
  return (
    <section className="aqsi-screen rmk-gate rmk-opening" aria-labelledby="rmk-opening-title">
      <header className="aqsi-toolbar">
        <h2 id="rmk-opening-title">
          <LoaderCircle className="aqsi-spinner" size={18} aria-hidden="true" />{' '}
          {opening.mode === 'continue' ? 'Продолжаем смену' : 'Открываем смену'}
        </h2>
      </header>
      <p className="aqsi-caption">
        <MonitorSmartphone size={14} aria-hidden="true" /> {opening.registerName}
        {opening.cashierName !== null && opening.cashierName !== '' && <> · кассир {opening.cashierName}</>}
      </p>
      <FeedbackHelp help={vm.feedbackHelp} onIncidents={actions.openIncidents} />

      <ol className="aqsi-panel rmk-opening__steps" aria-label="Ход открытия смены">
        {opening.steps.map((step) => (
          <li key={step.id} className="rmk-opening__step" data-state={step.state} aria-current={step.state === 'active' ? 'step' : undefined}>
            {STEP_ICONS[step.state]}
            <span className="rmk-opening__text">{step.text}</span>
            <span className="rmk-opening__state">{STEP_WORDS[step.state]}</span>
          </li>
        ))}
      </ol>
      <p className="aqsi-card__text" role="timer" aria-label="Сколько прошло">
        Прошло {elapsedText(opening.startedAt, now)}
      </p>
      {opening.hint !== null && (
        <p className="aqsi-card__text" role="status">
          {opening.hint}
        </p>
      )}

      <section className="aqsi-panel rmk-opening__server" aria-label="Что отвечает сервер">
        <h3>Что отвечает сервер</h3>
        {operation === null ? (
          <p className="aqsi-card__text" role="status">
            Ответа сервера по этой команде ещё нет: запрос отправляется. Если так долго — посмотрите точку «Связь» в шапке.
          </p>
        ) : (
          <>
            <p className="aqsi-card__text" role="status">
              <strong>{operation.stateText}</strong>
            </p>
            {operation.details.map((line) => (
              <p key={line} className="aqsi-card__text">
                {line}
              </p>
            ))}
          </>
        )}
      </section>

      {operation !== null && attention && (
        <OperationPanel
          operation={operation}
          onCheck={actions.checkOperation}
          onReconcile={actions.reconcileOperation}
          onDismiss={actions.dismissOperation}
        />
      )}
    </section>
  )
}
